import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Settings } from "lucide-react";

import { LANGUAGE_OPTIONS as LANG_OPTS } from "@/config/languages";
const LANGUAGE_OPTIONS = [
  { code: "auto", label: "Auto-detect" },
  ...LANG_OPTS.map(l => ({ code: l.code, label: l.name })),
];

export default function NoterSettingsModal({ settings, onSave, trigger }) {
  const [open, setOpen] = useState(false);
  const [local, setLocal] = useState(settings);

  const handleOpen = () => {
    setLocal(settings);
    setOpen(true);
  };

  const handleSave = () => {
    onSave?.(local);
    setOpen(false);
  };

  return (
    <>
      {trigger ? (
        <button onClick={handleOpen}>{trigger}</button>
      ) : (
        <Button onClick={handleOpen} variant="outline" size="sm">
          <Settings className="w-4 h-4 mr-1" />
          Settings
        </Button>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold">Noter Settings</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs font-semibold text-slate-600">Transcribed Language</Label>
              <Select
                value={local?.language || "auto"}
                onValueChange={(v) => setLocal({ ...local, language: v === "auto" ? null : v })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Auto-detect" />
                </SelectTrigger>
                <SelectContent>
                  {LANGUAGE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.code} value={opt.code}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs font-semibold text-slate-600">Transcription Mode</Label>
              <Select
                value={local?.transcriptionMode || "live"}
                onValueChange={(v) => setLocal({ ...local, transcriptionMode: v })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="live">Live Transcription (Real-time)</SelectItem>
                  <SelectItem value="on_stop">Transcribe Only on Record Stop</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs font-semibold text-slate-600">Live Translation Target</Label>
              <Select
                value={local?.targetLanguage || "none"}
                onValueChange={(v) => setLocal({ ...local, targetLanguage: v === "none" ? "" : v })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="None (No Translation)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None (No Translation)</SelectItem>
                  {LANGUAGE_OPTIONS.filter(o => o.code !== "auto").map((opt) => (
                    <SelectItem key={"target-" + opt.code} value={opt.code}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs font-semibold text-slate-600">Voice Synthesis (TTS Stream Reader)</Label>
              <Select
                value={local?.ttsVoice || "off"}
                onValueChange={(v) => setLocal({ ...local, ttsVoice: v })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Disabled" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="off">Disabled</SelectItem>
                  <SelectItem value="female">Female Voice</SelectItem>
                  <SelectItem value="male">Male Voice</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[10px] text-slate-400 mt-1">
                Reads the translated/original messages automatically using local speech voices.
              </p>
            </div>

            <div>
              <Label className="text-xs font-semibold text-slate-600">Meeting Title</Label>
              <Input
                className="mt-1"
                value={local?.title || ""}
                onChange={(e) => setLocal({ ...local, title: e.target.value })}
                placeholder="Untitled Meeting"
              />
            </div>

            <div>
              <Label className="text-xs font-semibold text-slate-600">Sample Rate (Hz)</Label>
              <Select
                value={String(local?.sampleRate || 16000)}
                onValueChange={(v) => setLocal({ ...local, sampleRate: parseInt(v) })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="8000">8000 Hz (narrowband)</SelectItem>
                  <SelectItem value="16000">16000 Hz (wideband)</SelectItem>
                  <SelectItem value="44100">44100 Hz (CD quality)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} className="bg-violet-600 hover:bg-violet-700">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
